import { aiJson } from "@/services/ai.service";
import type { TopicSources } from "@/services/source.service";
import type { WebSource } from "./webSource";

/**
 * Aufgabenblatt einer Probeklausur.
 *
 * Zwei Regeln, die vorher gefehlt haben:
 *   1. Der Operator MUSS zum Anforderungsbereich passen. „Nenne … und beurteile
 *      dabei" ist keine AFB-I-Aufgabe; solche Mischformen machen die Bewertung
 *      hinterher beliebig. Deshalb bekommt die KI die amtlichen Operatorenlisten
 *      vorgegeben und darf nur daraus wählen.
 *   2. Die Aufgaben beziehen sich auf das MITGELIEFERTE Material – der Quelltext
 *      kommt aus dem Netz (siehe webSource.ts) und wird nie umgeschrieben.
 */

/** Operatoren nach Anforderungsbereich (KMK-Bildungsstandards). */
export const OPERATORS: Record<1 | 2 | 3, string[]> = {
  1: ["Nenne", "Benenne", "Gib an", "Beschreibe", "Fasse zusammen", "Stelle dar", "Skizziere", "Ordne zu"],
  2: [
    "Erkläre", "Erläutere", "Analysiere", "Untersuche", "Vergleiche", "Charakterisiere",
    "Arbeite heraus", "Begründe", "Ordne ein", "Interpretiere", "Belege", "Weise nach",
  ],
  3: [
    "Beurteile", "Bewerte", "Nimm Stellung", "Erörtere", "Diskutiere", "Prüfe",
    "Entwirf", "Gestalte", "Setze dich auseinander mit",
  ],
};

const OPERATOR_DOC =
  "OPERATOREN – wähle je Aufgabe GENAU EINEN und stelle ihn an den Anfang der Aufgabe:\n" +
  `AFB I (Reproduktion): ${OPERATORS[1].join(", ")}\n` +
  `AFB II (Reorganisation, Transfer): ${OPERATORS[2].join(", ")}\n` +
  `AFB III (Werten, Urteilen, Gestalten): ${OPERATORS[3].join(", ")}\n` +
  "Ein Operator aus einem anderen Bereich als dem angegebenen afb-Wert ist ein Fehler. " +
  "Verbinde niemals zwei Operatoren in einer Aufgabe.";

export interface ExamTask {
  question: string;
  afb: 1 | 2 | 3;
  operator: string;
  points: number;
  expectation: string;
}

export interface ExamPaper {
  sourceTitle: string;
  source: string;
  citation: string;
  sourceUrl: string;
  tasks: ExamTask[];
}

interface RawPaper {
  sourceTitle?: string;
  tasks?: Partial<ExamTask>[];
}

/**
 * Aufgaben zu einem bereits geholten Originaltext. Der Text selbst geht
 * unverändert ins Blatt – die KI liefert nur die Aufgaben dazu.
 */
export async function buildPaper(
  topicTitle: string,
  web: WebSource,
  src: TopicSources,
  examType?: string,
  taskCount = 3
): Promise<ExamPaper> {
  const raw = await aiJson<RawPaper>(
    "Du bist Lehrkraft und schreibst die Aufgaben einer Probeklausur zu einem VORGEGEBENEN Material. " +
      "Das Material steht fest und wird NICHT verändert, gekürzt oder neu formuliert.\n\n" +
      `${OPERATOR_DOC}\n\n` +
      `AUFBAU: genau ${taskCount} Aufgaben, aufsteigend nach Anforderungsbereich, jede bezieht sich ` +
      "direkt auf das Material und ist ohne Zusatzwissen lösbar. Die Punkte steigen mit dem " +
      "Anforderungsbereich (Richtwert 8 / 14 / 18 / 22).\n" +
      "Der Unterricht des Schülers (unten als NOTIZEN) bestimmt Schwerpunkt und Fachsprache – " +
      "frage nichts ab, was dort nie vorkam.\n" +
      'Gib NUR JSON zurück: {"sourceTitle":"Material 1: …","tasks":[{"question":"vollständige ' +
      'Aufgabenstellung, beginnt mit dem Operator","afb":1,"operator":"Nenne","points":8,' +
      '"expectation":"Erwartungshorizont in Stichpunkten"}]}\nDeutsch.',
    `THEMA: ${topicTitle}${examType ? `\nPRÜFUNGSFORM: ${examType}` : ""}\n\n` +
      `MATERIAL (${web.kind}):\n${web.title}\n\n${web.text}\n\n` +
      // Gesenkt (war 12000) – zusammen mit dem Prüfungstext und dem
      // Antwort-Budget muss die Anfrage unter Groqs Minuten-Kontingent
      // bleiben (siehe GROQ_SAFE_PROMPT_TOKENS in ai.service.ts).
      `NOTIZEN DES SCHÜLERS (Schwerpunkt und Fachsprache):\n${src.text.slice(0, 6000)}`,
    3000,
    "medium"
  );

  return {
    sourceTitle: (raw.sourceTitle ?? "").trim() || `Material 1: ${web.title}`,
    source: web.text,
    citation: web.citation,
    sourceUrl: web.url,
    tasks: normalizeTasks(raw.tasks),
  };
}

/**
 * Operator und AFB in Einklang bringen. Passt der genannte Operator nicht zum
 * Anforderungsbereich, entscheidet der Operator – er steht schließlich in der
 * Aufgabenstellung, die der Schüler liest.
 */
function normalizeTasks(list: Partial<ExamTask>[] | undefined): ExamTask[] {
  const tasks = (Array.isArray(list) ? list : []).map((t, i) => {
    const question = String(t.question ?? "").trim() || `Aufgabe ${i + 1}`;
    const stated = String(t.operator ?? "").trim();
    const fromText = detectOperator(question);
    const operator = fromText || stated || OPERATORS[1][0];

    let afb = ([1, 2, 3] as const).find((a) => OPERATORS[a].some((o) => eq(o, operator)));
    if (!afb) {
      const n = Number(t.afb);
      afb = n === 2 || n === 3 ? (n as 2 | 3) : ((Math.min(i + 1, 3) as 1 | 2 | 3));
    }

    const points = Number(t.points);
    return {
      question,
      afb,
      operator,
      points: points > 0 ? Math.round(points) : [8, 14, 18, 22][Math.min(i, 3)],
      expectation: String(t.expectation ?? "").trim(),
    };
  });
  // Aufsteigend, damit das Blatt der üblichen Klausurdramaturgie folgt.
  return tasks.sort((a, b) => a.afb - b.afb);
}

const eq = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

/** Ersten Operator aus der Aufgabenstellung lesen (steht immer vorn). */
function detectOperator(question: string): string {
  const all = [...OPERATORS[3], ...OPERATORS[2], ...OPERATORS[1]].sort((a, b) => b.length - a.length);
  const head = question.slice(0, 40).toLowerCase();
  return all.find((op) => head.startsWith(op.toLowerCase())) ?? "";
}
